import type { Logger } from "@expert/logging";
import { setGlobalConfig } from "@simplrsupport/headless-chat-web";
import { useEffect, useMemo } from "react";
import { getGlobalConfigFromParams } from "./queryParams";
import { startSimplrSession } from "./sessionHandling";
import { type LogicBuilderGlobalConfig, SIMPLR_PUBLIC_SERVER_DOMAIN } from "./types";

interface SimplrInitializersProps {
    callSid?: string;
    globalConfig?: LogicBuilderGlobalConfig;
    logger: Logger;
    sessionId: string;
}

export function useSimplrInitializers({ globalConfig, logger: loggerProp, sessionId }: SimplrInitializersProps) {
    const isNonprod = import.meta.env.VITE_ENV_TYPE !== "production";

    const queryParams = getGlobalConfigFromParams(isNonprod);

    const customFlowSystemId = queryParams?.customFlowSystemId ?? globalConfig?.customFlowSystemId;
    const publicServerDomain = queryParams?.publicServerDomain ?? SIMPLR_PUBLIC_SERVER_DOMAIN;
    const widgetId = queryParams?.widgetId ?? globalConfig?.widgetId;

    const logger = useMemo(
        () =>
            loggerProp.child({
                customFlowSystemId,
                module: "useSimplrInitializers",
                publicServerDomain,
                queryParams,
                widgetId,
            }),
        [customFlowSystemId, loggerProp, publicServerDomain, queryParams, widgetId],
    );

    useEffect(() => {
        if (!publicServerDomain || !widgetId) {
            return;
        }

        logger.info("Solve Guide | Setting Simplr Global Config");

        // TODO: add back handleError (EWS-1409)[https://solutonashville.atlassian.net/browse/EWS-1409]
        setGlobalConfig({
            publicServerDomain,
            widgetId,
            publicServerPrefix: "/chat-api",
            analytics: {
                trackEvent: console.log,
            },
            calculateProgress: true,
            customFlowSystemId,
        });

        logger.info("Solve Guide | Calling start session with Simplr immediately on session start");
        startSimplrSession({ metadata: {}, sessionId });
        // oxlint-disable-next-line exhaustive-deps
    }, [sessionId]);
}
